import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useUnsavedChanges } from "@/lib/unsavedChanges";
import { toast } from "sonner";
import { FileCheck2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContractEditorPanel } from "@/components/ContractEditorPanel";
import { ConditionsEditorPanel } from "@/components/ConditionsEditorPanel";

export default function ContractsAdmin() {
  const [terms, setTerms] = useState("");
  const [savedTerms, setSavedTerms] = useState("");
  const [contract, setContract] = useState("");
  const [savedContract, setSavedContract] = useState("");
  const [draft, setDraft] = useState(null);
  const [versions, setVersions] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [confirm, setConfirm] = useState(false);
  const pendingPublish = useRef(null);
  const nextVersion = `1.${versions[0]?.sequence ?? 0}`;

  const load = async () => {
    try {
      const [policies, contracts] = await Promise.all([
        api.get("/admin/policies"), api.get("/admin/contracts"),
      ]);
      const text = policies.data.terms_html || "";
      const versions = contracts.data.versions || [];
      const current = contracts.data.draft?.html ?? versions[0]?.html ?? "";
      setTerms(text); setSavedTerms(text);
      setVersions(versions); setFields(contracts.data.placeholders || []);
      setDraft(contracts.data.draft || null);
      setContract(current); setSavedContract(current);
    } catch { toast.error("Impossibile caricare i contratti"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveTerms = async () => {
    setSaving("terms");
    try {
      await api.put("/admin/policies", { terms_html: terms });
      setSavedTerms(terms);
      toast.success("Condizioni di utilizzo salvate");
      return true;
    } catch (error) { toast.error(error.response?.data?.detail || "Salvataggio non riuscito"); return false; }
    finally { setSaving(""); }
  };

  const saveDraft = async () => {
    if (saving) return false;
    const text = contract;
    setSaving("draft");
    try {
      const { data } = await api.put("/admin/contracts/draft", { html: text });
      setDraft(data); setSavedContract(data.html);
      setContract((current) => current === text ? data.html : current);
      toast.success("Bozza del contratto salvata");
      return true;
    } catch (error) { toast.error(error.response?.data?.detail || "Impossibile salvare la bozza"); return false; }
    finally { setSaving(""); }
  };

  useUnsavedChanges(!loading && contract !== savedContract, saveDraft,
    () => setContract(savedContract), "Il contratto contiene modifiche non salvate.");
  useUnsavedChanges(!loading && terms !== savedTerms, saveTerms,
    () => setTerms(savedTerms), "Le condizioni di utilizzo contengono modifiche non salvate.");

  const publish = async () => {
    setSaving("publish");
    try {
      if (!pendingPublish.current || pendingPublish.current.html !== contract) {
        pendingPublish.current = { html: contract, id: crypto.randomUUID() };
      }
      const { data } = await api.post("/admin/contracts/publish", {
        html: contract, publish_id: pendingPublish.current.id,
        draft_updated_at: draft?.updated_at ?? null,
      });
      pendingPublish.current = null;
      setConfirm(false);
      await load();
      toast.success(`Contratto v${data.version} pubblicato`);
    } catch (error) { toast.error(error.response?.data?.detail || "Pubblicazione non riuscita"); }
    finally { setSaving(""); }
  };

  return <div className="px-5 py-10 md:px-12 md:py-12 max-w-7xl" data-testid="contracts-admin-page">
    <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#8A8579] mb-2">
      <FileCheck2 className="w-4 h-4" /> Documenti legali
    </div>
    <h1 className="font-serif text-4xl mb-8">Contratti e condizioni</h1>
    {loading ? <p className="text-sm text-[#707070]" data-testid="contracts-loading">Caricamento…</p> :
      <Tabs defaultValue="contract" data-testid="contracts-tabs">
        <TabsList className="flex h-auto w-full sm:w-fit justify-start items-stretch gap-0 rounded-none border-b border-[#E5E5E0] bg-transparent p-0 mb-6">
          <TabsTrigger value="contract" data-testid="contracts-tab-contract"
            className="min-w-0 flex-1 sm:flex-none whitespace-normal text-left rounded-none border-b-2 border-transparent bg-transparent px-3 sm:px-6 py-3 text-sm shadow-none data-[state=active]:border-[#1A1A1A] data-[state=active]:bg-transparent data-[state=active]:shadow-none">Contratto</TabsTrigger>
          <TabsTrigger value="terms" data-testid="contracts-tab-terms"
            className="min-w-0 flex-1 sm:flex-none whitespace-normal text-left rounded-none border-b-2 border-transparent bg-transparent px-3 sm:px-6 py-3 text-sm shadow-none data-[state=active]:border-[#1A1A1A] data-[state=active]:bg-transparent data-[state=active]:shadow-none">Condizioni di utilizzo</TabsTrigger>
        </TabsList>
        <TabsContent value="contract" data-testid="contracts-contract-panel">
          <ContractEditorPanel contract={contract} setContract={setContract} versions={versions}
            fields={fields} onSaveDraft={saveDraft} onPublish={() => setConfirm(true)}
            saving={saving} savedContract={savedContract} draft={draft} nextVersion={nextVersion} />
        </TabsContent>
        <TabsContent value="terms" data-testid="contracts-terms-panel">
          <ConditionsEditorPanel terms={terms} setTerms={setTerms} onSave={saveTerms} saving={saving} />
        </TabsContent>
      </Tabs>}
    <Dialog open={confirm} onOpenChange={setConfirm}>
      <DialogContent data-testid="contract-publish-confirmation">
        <DialogHeader><DialogTitle>Pubblicare la versione {nextVersion}?</DialogTitle>
          <DialogDescription className="text-sm text-[#707070]">Il testo pubblicato non potrà più essere modificato. Gli amministratori dei clienti dovranno leggere e accettare la nuova versione al prossimo accesso.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirm(false)} data-testid="contract-publish-cancel">Annulla</Button>
          <Button onClick={publish} disabled={!!saving}
            className="bg-[#1A1A1A] text-white hover:bg-black focus-visible:ring-[#C5B39B]"
            data-testid="contract-publish-confirm">
            {saving === "publish" ? "Pubblicazione…" : "Conferma e pubblica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>;
}